import React, { useState, useMemo, useCallback, useRef, useEffect } from 'react'
import { useTranslation } from '../../../shared/hooks/useIntractionTranslate'
import { useNavigate } from 'react-router-dom'
import { PlusIcon, MagnifyingGlassIcon, Cog6ToothIcon } from '@heroicons/react/24/outline'
import { Box } from '@mui/material'
import { PageWrapper } from '../../../shared/components'
import { Button, Input, Spinner } from '../../../interaxDesignSystemComponents'
import { useInfiniteTrainingSprints } from '../../../apiLayer'
import { TrainingSprint, TrainingSprintStatus, TrainingSprintSection } from '../../../types/TrainingSprintTypes'
import { TrainingSprintAccordion } from '../../../components/TrainingSprints'
import { INTERAX_COLORS } from '../../../constants/colors'
import { PageHeader } from '../../../shared/components/PageHeader'
import { usePrefetchSprintWizardConfig } from '../facilitatorTrainingSprintCreation/hooks/usePrefetchSprintWizardConfig'
import { useBlockOnPendingWithoutData } from '../../organization/orgSwitch'

export const TrainingSprintManagementPage: React.FC = () => {
  usePrefetchSprintWizardConfig()
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [searchTerm, setSearchTerm] = useState('')
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('')
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({
    drafts: true,
    'active-upcoming': true,
    completed: true
  })

  // Debounce search term
  const debounceTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const debouncedSetSearchTerm = useCallback((value: string) => {
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current)
    }
    debounceTimeoutRef.current = setTimeout(() => {
      setDebouncedSearchTerm(value)
    }, 300)
  }, [])

  // Paged fetch with server-side search; the first page carries every ACTIVE sprint up-front.
  const { data, isPending, isFetching, isFetchingNextPage, hasNextPage, fetchNextPage, error } =
    useInfiniteTrainingSprints(debouncedSearchTerm)

  // Merge the all-actives payload with the paged stream; dedupe since actives reappear in later pages.
  const trainingSprints = useMemo<TrainingSprint[]>(() => {
    const byId = new Map<string, TrainingSprint>()
    for (const sprint of data?.pages[0]?.data?.activeSprints ?? []) {
      byId.set(sprint.id, sprint)
    }
    for (const page of data?.pages ?? []) {
      for (const sprint of page.data?.sprints ?? []) {
        if (!byId.has(sprint.id)) byId.set(sprint.id, sprint)
      }
    }
    return Array.from(byId.values())
  }, [data])
  const totalSprints = data?.pages[0]?.data?.total ?? trainingSprints.length

  const hasSprintListData = data !== undefined
  const showBlockingLoader = useBlockOnPendingWithoutData(isPending, hasSprintListData)
  const isRefreshingList = isFetching && !isFetchingNextPage && hasSprintListData

  const loadMoreRef = useRef<HTMLDivElement | null>(null)
  useEffect(() => {
    const node = loadMoreRef.current
    if (!node || !hasNextPage) return
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting && hasNextPage && !isFetchingNextPage) {
          fetchNextPage()
        }
      },
      { rootMargin: '200px' }
    )
    observer.observe(node)
    return () => observer.disconnect()
  }, [hasNextPage, isFetchingNextPage, fetchNextPage])

  // Group sprints by status
  const sprintSections = useMemo((): TrainingSprintSection[] => {
    const drafts = trainingSprints.filter((sprint) => sprint.status === TrainingSprintStatus.INACTIVE)
    const activeUpcoming = trainingSprints.filter((sprint) => sprint.status === TrainingSprintStatus.ACTIVE)
    const completed = trainingSprints.filter((sprint) => sprint.status === TrainingSprintStatus.COMPLETED)

    return [
      {
        id: 'drafts',
        title: t('facilitator.trainingSprintManagement.drafts'),
        count: drafts.length,
        isExpanded: expandedSections.drafts,
        sprints: drafts
      },
      {
        id: 'active-upcoming',
        title: t('facilitator.trainingSprintManagement.activeAndUpcoming'),
        count: activeUpcoming.length,
        isExpanded: expandedSections['active-upcoming'],
        sprints: activeUpcoming
      },
      {
        id: 'completed',
        title: t('facilitator.trainingSprintManagement.completedSprints'),
        count: completed.length,
        isExpanded: expandedSections.completed,
        sprints: completed
      }
    ]
  }, [trainingSprints, expandedSections, t])

  const handleCreateSprint = () => {
    navigate('/facilitator/sprint-creation')
  }

  const handleContinueSprint = (sprint: TrainingSprint) => {
    // Navigate to sprint creation with sprint ID and initial data in location state
    // The SprintCreation component will handle loading and transforming the sprint

    // Note: If we have the full sprint data, we could transform it here
    // But typically we'll want to fetch the latest version from the backend
    navigate('/facilitator/sprint-creation', {
      state: {
        sprintId: sprint.id
      }
    })
  }

  const handleViewDetails = (sprint: TrainingSprint) => {
    navigate(`/facilitator/sprint/${sprint.id}`)
  }

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value
    setSearchTerm(value)
    debouncedSetSearchTerm(value)
  }

  const handleToggleSection = (sectionId: string) => {
    setExpandedSections((prev) => ({
      ...prev,
      [sectionId]: !prev[sectionId]
    }))
  }

  if (showBlockingLoader) {
    return (
      <PageWrapper>
        <Spinner size="large" color="sun" text={t('facilitator.trainingSprintManagement.loadingSprints')} centered />
      </PageWrapper>
    )
  }

  if (error) {
    return (
      <PageWrapper sx={{ textAlign: 'center' }}>
        <Box className="text-body-1" sx={{ color: INTERAX_COLORS.sun }}>
          {t('facilitator.trainingSprintManagement.errorLoading', { message: error.message })}
        </Box>
      </PageWrapper>
    )
  }

  return (
    <PageWrapper sx={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
      {/* Header with Title, Description, and Button */}
      <PageHeader
        badgeText="TRAINING SPRINTS"
        badgeIcon={<Cog6ToothIcon />}
        title={t('facilitator.trainingSprintManagement.title')}
        subtitle={t('facilitator.trainingSprintManagement.description')}
        actions={
          <Button
            variant="primary"
            onClick={handleCreateSprint}
            icon={<PlusIcon className="w-4 h-4" />}
            iconPosition="left"
            data-testid="new-sprint-btn"
          >
            {t('facilitator.trainingSprintManagement.createSprint')}
          </Button>
        }
      />

      {/* Search Input */}
      <Box
        sx={{
          backgroundColor: INTERAX_COLORS.heritageBlue40,
          borderRadius: '8px',
          padding: '16px',
          display: 'flex',
          justifyContent: 'center',
          width: '100%'
        }}
      >
        <Box sx={{ width: '100%' }}>
          <Input
            type="search"
            value={searchTerm}
            onChange={handleSearchChange}
            placeholder={t('facilitator.trainingSprintManagement.searchPlaceholder')}
            startIcon={<MagnifyingGlassIcon className="w-5 h-5" />}
            fullWidth
            size="medium"
          />
        </Box>
      </Box>

      {/* Inline loading indicator for search filtering */}
      {isRefreshingList && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Spinner size="small" color="sun" />
            <Box sx={{ color: INTERAX_COLORS.white, fontSize: '14px' }}>Searching...</Box>
          </Box>
        </Box>
      )}

      {/* Accordion Sections */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: '16px',
          opacity: isRefreshingList ? 0.5 : 1,
          transition: 'opacity 0.2s ease'
        }}
      >
        {sprintSections.map((section) => (
          <TrainingSprintAccordion
            key={section.id}
            section={section}
            onToggle={handleToggleSection}
            onContinue={handleContinueSprint}
            onViewDetails={handleViewDetails}
          />
        ))}
      </Box>

      {/* Infinite scroll: sentinel auto-loads, button is the accessible fallback */}
      {(hasNextPage || isFetchingNextPage) && (
        <Box
          ref={loadMoreRef}
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: '8px',
            py: { xs: 2, sm: 2, md: 3 }
          }}
        >
          {isFetchingNextPage ? (
            <Spinner size="small" color="sun" text={t('facilitator.trainingSprintManagement.loadingMore')} />
          ) : (
            <Button variant="primary" onClick={() => fetchNextPage()}>
              {t('facilitator.trainingSprintManagement.loadMore')}
            </Button>
          )}
          <Box sx={{ color: INTERAX_COLORS.thunderGrey, fontSize: '13px' }}>
            {t('facilitator.trainingSprintManagement.showingCount', {
              shown: trainingSprints.length,
              total: totalSprints
            })}
          </Box>
        </Box>
      )}
    </PageWrapper>
  )
}
